"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { FiHome, FiLogOut, FiBell, FiCheck, FiX } from "react-icons/fi"
import {
  FaUsers,
  FaCalendarAlt,
  FaTasks,
  FaClipboardList,
  FaMicrophone,
  FaUserCog,
  FaVideo,
  FaCheck,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner
} from "react-icons/fa"
import { HiMenu, HiX } from "react-icons/hi"
import PropTypes from "prop-types"
import image from "../assets/im1.png"

const FALLBACK_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%235e17eb'/%3E%3Ctext x='20' y='25' text-anchor='middle' fill='white' font-size='16' font-family='Arial' dy='.3em'%3E%3C/text%3E%3C/svg%3E"

const Layout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [authData, setAuthData] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [photoUrl, setPhotoUrl] = useState(null)

  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [processingInvitation, setProcessingInvitation] = useState(null)

  const navigate = useNavigate()
  const location = useLocation()
  const dropdownRef = useRef(null)
  const notificationRef = useRef(null)
  const intervalRef = useRef(null)

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

  useEffect(() => {
    const loadAuthData = () => {
      const storedAuthData = localStorage.getItem("auth_data")
      if (storedAuthData) {
        try {
          const parsedAuthData = JSON.parse(storedAuthData)
          setAuthData(parsedAuthData)

          if (parsedAuthData.photo) {
            let photoFullUrl = parsedAuthData.photo
            if (!photoFullUrl.startsWith('http') && !photoFullUrl.startsWith('data:')) {
              photoFullUrl = `${API_URL}${photoFullUrl}`
            }
            setPhotoUrl(photoFullUrl)
          } else {
            setPhotoUrl(null)
          }
        } catch (e) {
          console.error("Erreur parsing authData:", e)
        }
      }
    }

    loadAuthData()

    const handleStorageChange = (e) => {
      if (e.key === 'auth_data') {
        loadAuthData()
      }
    }

    const handleCustomUpdate = () => {
      loadAuthData()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-updated', handleCustomUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-updated', handleCustomUpdate)
    }
  }, [])

  // Listen for profile photo updates
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      if (event.detail) {
        const updatedAuthData = event.detail
        setAuthData(updatedAuthData)
        
        if (updatedAuthData.photo) {
          let photoFullUrl = updatedAuthData.photo
          if (!photoFullUrl.startsWith('http') && !photoFullUrl.startsWith('data:')) {
            photoFullUrl = `${API_URL}${photoFullUrl}`
          }
          setPhotoUrl(photoFullUrl)
        }
      }
    }
    
    window.addEventListener('user-profile-updated', handleProfileUpdate)
    window.addEventListener('auth-updated', () => {
      const storedAuthData = localStorage.getItem("auth_data")
      if (storedAuthData) {
        const parsed = JSON.parse(storedAuthData)
        setAuthData(parsed)
        if (parsed.photo) {
          let photoFullUrl = parsed.photo
          if (!photoFullUrl.startsWith('http') && !photoFullUrl.startsWith('data:')) {
            photoFullUrl = `${API_URL}${photoFullUrl}`
          }
          setPhotoUrl(photoFullUrl)
        }
      }
    })
    
    return () => {
      window.removeEventListener('user-profile-updated', handleProfileUpdate)
    }
  }, [])

  // Fonction pour rafraîchir le compteur (optimisée)
  const refreshUnreadCount = useCallback(async () => {
    if (!authData?.accessToken) return

    try {
      const response = await fetch(`${API_URL}/notifications/unread-count/`, {
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        const newCount = data.count || 0
        if (newCount !== unreadCount) {
          setUnreadCount(newCount)
        }
        return newCount
      }
    } catch (error) {
      console.error("Erreur chargement compteur:", error)
    }
    return 0
  }, [authData, unreadCount])

  // Fonction pour rafraîchir les notifications (optimisée)
  const refreshNotifications = useCallback(async () => {
    if (!authData?.accessToken) return

    setLoadingNotifications(true)
    try {
      const response = await fetch(`${API_URL}/notifications/?limit=10`, {
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        let notifs = []
        if (Array.isArray(data)) {
          notifs = data
        } else if (data.results && Array.isArray(data.results)) {
          notifs = data.results
        } else if (data.data && Array.isArray(data.data)) {
          notifs = data.data
        }
        setNotifications(notifs)
      }
    } catch (error) {
      console.error("Erreur chargement notifications:", error)
    } finally {
      setLoadingNotifications(false)
    }
  }, [authData])

  // Chargement initial
  useEffect(() => {
    if (authData?.accessToken) {
      refreshUnreadCount()
      refreshNotifications()

      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = setInterval(() => {
        refreshUnreadCount()
      }, 30000)

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }
  }, [authData, refreshUnreadCount, refreshNotifications])

  // Rafraîchir les notifications quand le dropdown est ouvert
  useEffect(() => {
    if (showNotifications) {
      refreshNotifications()
    }
  }, [showNotifications, refreshNotifications])

  // Écouter les événements de mise à jour
  useEffect(() => {
    const handleNotificationsUpdate = () => {
      refreshUnreadCount()
      if (showNotifications) {
        refreshNotifications()
      }
    }

    const handleInvitationUpdated = () => {
      refreshUnreadCount()
      if (showNotifications) {
        refreshNotifications()
      }
    }

    window.addEventListener('notifications-updated', handleNotificationsUpdate)
    window.addEventListener('invitation-updated', handleInvitationUpdated)
    window.addEventListener('refresh-notifications', handleNotificationsUpdate)

    return () => {
      window.removeEventListener('notifications-updated', handleNotificationsUpdate)
      window.removeEventListener('invitation-updated', handleInvitationUpdated)
      window.removeEventListener('refresh-notifications', handleNotificationsUpdate)
    }
  }, [showNotifications, refreshUnreadCount, refreshNotifications])

  const respondToInvitation = async (notificationId, reponse) => {
    setProcessingInvitation(notificationId)
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}/repondre_invitation/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reponse }),
      })

      if (response.ok) {
        refreshUnreadCount()
        refreshNotifications()
        
        window.dispatchEvent(new CustomEvent('invitation-updated', {
          detail: { type: reponse }
        }))

        if (location.pathname === '/mes-reunions') {
          window.dispatchEvent(new Event('refresh-meetings'))
        }
      }
    } catch (error) {
      console.error("Erreur réponse invitation:", error)
    } finally {
      setProcessingInvitation(null)
    }
  }

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}/mark_read/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        refreshUnreadCount()
        refreshNotifications()
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      }
    } catch (error) {
      console.error("Erreur marquage notification:", error)
    }
  }

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${API_URL}/notifications/mark_all_read/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        refreshUnreadCount()
        refreshNotifications()
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      }
    } catch (error) {
      console.error("Erreur marquage toutes notifications:", error)
    }
  }

  const fetchUserPhoto = async (userId, token) => {
    try {
      const response = await fetch(`${API_URL}/users/${userId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (response.ok) {
        const userData = await response.json()
        if (userData.photo_url) {
          const photoFullUrl = userData.photo_url.startsWith('http')
            ? userData.photo_url
            : `${API_URL}${userData.photo_url}`
          setPhotoUrl(photoFullUrl)

          const updatedAuthData = { ...authData, photo: photoFullUrl }
          localStorage.setItem("auth_data", JSON.stringify(updatedAuthData))
          setAuthData(updatedAuthData)
        }
      }
    } catch (error) {
      console.error("Erreur chargement photo:", error)
    }
  }

  useEffect(() => {
    if (!photoUrl && authData?.userId && authData?.accessToken) {
      fetchUserPhoto(authData.userId, authData.accessToken)
    }
  }, [authData])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleLogout = () => {
    setShowLogoutConfirm(true)
    setShowDropdown(false)
  }

  const confirmLogout = () => {
    localStorage.removeItem("access_token")
    localStorage.removeItem("refresh_token")
    localStorage.removeItem("auth_data")
    setAuthData(null)
    setPhotoUrl(null)
    setNotifications([])
    setUnreadCount(0)
    setShowLogoutConfirm(false)
    navigate("/")
  }

  const getMenuItems = () => {
    const role = authData?.role;

    if (role === "Admin") {
      return [
        { path: "/admin-dashboard", name: "Dashboard", icon: <FiHome size={18} /> },
        { path: "/notifications", name: "Notifications", icon: <FiBell size={18} />, badge: unreadCount },
        { path: "/listeUtilisateurs", name: "Utilisateurs", icon: <FaUsers size={18} /> },
        { path: "/roles-permissions", name: "Rôles & Permissions", icon: <FaUserCog size={18} /> },
        { path: "/admin-reunions", name: "Réunions", icon: <FaCalendarAlt size={18} /> },
      ];
    }

    if (role === "Manager") {
      return [
        { path: "/dashboard", name: "Dashboard", icon: <FiHome size={18} /> },
        { path: "/notifications", name: "Notifications", icon: <FiBell size={18} />, badge: unreadCount },
        { path: "/listeReunions", name: "Réunions", icon: <FaCalendarAlt size={18} /> },
        { path: "/comptes-rendus", name: "Comptes-rendus", icon: <FaClipboardList size={18} /> },
        { path: "/taches", name: "Tâches", icon: <FaTasks size={18} /> },
      ];
    }

    if (role === "Employé") {
      return [
        { path: "/consulter-reunion", name: "Dashboard", icon: <FaVideo size={18} /> },
        { path: "/notifications", name: "Notifications", icon: <FiBell size={18} />, badge: unreadCount },
        { path: "/mes-reunions", name: "Mes Réunions", icon: <FaCalendarAlt size={18} /> },
        { path: "/mes-taches", name: "Mes Tâches", icon: <FaTasks size={18} /> },
      ];
    }

    return [];
  };

  const menuItems = getMenuItems();

  const getInitial = () => {
    return authData?.name?.charAt(0)?.toUpperCase() || 'U'
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'invitation_reunion':
        return <FaCalendarAlt className="text-[#5e17eb]" size={16} />
      case 'reunion':
        return <FaCalendarAlt className="text-[#5e17eb]" size={16} />
      case 'reunion_validee':
        return <FaCheckCircle className="text-green-500" size={16} />
      case 'reunion_rejetee':
        return <FaTimesCircle className="text-red-500" size={16} />
      case 'reunion_creee':
        return <FaCalendarAlt className="text-[#5e17eb]" size={16} />
      case 'validation_requise':
        return <FaSpinner className="text-yellow-500 animate-spin" size={16} />
      case 'tache':
        return <FaTasks className="text-green-500" size={16} />
      case 'compte_rendu':
        return <FaClipboardList className="text-blue-500" size={16} />
      case 'reponse_invitation':
        return <FaCheck className="text-green-500" size={16} />
      default:
        return <FiBell className="text-gray-500" size={16} />
    }
  }

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "À l'instant"
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours} h`
    if (diffDays === 1) return "Hier"
    return `Il y a ${diffDays} jours`
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden fixed top-4 left-4 z-50 bg-white text-[#5e17eb] p-2.5 rounded-full shadow-lg hover:bg-[#5e17eb] hover:text-white transition-all duration-300"
        aria-label={isOpen ? "Close menu" : "Open menu"}
      >
        {isOpen ? <HiX size={20} /> : <HiMenu size={20} />}
      </button>

      <div
        className={`
          fixed top-0 left-0 h-screen bg-white shadow-xl z-40
          transition-all duration-300 ease-in-out
          ${isOpen ? "w-72 translate-x-0" : "-translate-x-full md:translate-x-0 md:w-72"}
        `}
      >
        <div className="flex justify-center items-center h-20 border-b border-gray-100">
          <img src={image} alt="Logo" className="max-h-16 max-w-full object-contain" />
        </div>

        <div className="p-5 overflow-y-auto h-[calc(100vh-5rem)]">
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 px-3">
              {authData?.role === "Admin" ? "Administration" :
                authData?.role === "Manager" ? "Gestion" :
                  authData?.role === "Employé" ? "Espace Employé" : "Menu"}
            </h3>
            <nav>
              <ul className="space-y-1.5">
                {menuItems.map((menuItem, index) => (
                  <li key={index}>
                    <NavLink
                      to={menuItem.path}
                      className={({ isActive }) => `
                        flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
                        ${isActive
                          ? "bg-gradient-to-r from-[#5e17eb]/20 to-[#5e17eb]/5 text-[#5e17eb] font-medium"
                          : "text-gray-600 hover:bg-gray-50 hover:text-[#5e17eb]"
                        }
                      `}
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="flex items-center">
                        <span className={`mr-3 ${location.pathname === menuItem.path ? "text-[#5e17eb]" : "text-gray-500"}`}>
                          {menuItem.icon}
                        </span>
                        <span className="text-sm font-medium">{menuItem.name}</span>
                      </div>

                      {menuItem.path === "/notifications" && unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full ml-2 animate-pulse">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}

                      {location.pathname === menuItem.path && (
                        <span className="h-2 w-2 rounded-full bg-[#5e17eb]"></span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:ml-72 transition-all duration-300">
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="px-6 py-4 flex items-center justify-end">
            <div className="flex items-center space-x-6">

              {/* NOTIFICATION BUTTON */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`
                    relative p-2.5 rounded-full transition-all duration-300 focus:outline-none group
                    ${unreadCount > 0 
                      ? 'bg-[#5e17eb]/10 shadow-md shadow-[#5e17eb]/20 hover:bg-[#5e17eb]/20' 
                      : 'hover:bg-gray-100'
                    }
                  `}
                  aria-label="Notifications"
                >
                  <FiBell
                    size={22}
                    className={`
                      transition-all duration-300
                      ${showNotifications 
                        ? 'text-[#5e17eb] scale-110' 
                        : unreadCount > 0 
                          ? 'text-[#5e17eb]' 
                          : 'text-gray-600 group-hover:text-[#5e17eb]'
                      }
                    `}
                  />

                  {unreadCount > 0 && (
                    <>
                      <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full ring-2 ring-white animate-ping"></span>
                      <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full ring-2 ring-white"></span>
                      
                      <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-gradient-to-r from-red-500 to-red-600 rounded-full text-xs text-white font-bold flex items-center justify-center shadow-lg ring-2 ring-white transform transition-transform hover:scale-110">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    </>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-in slide-in-from-top-2 fade-in duration-200">
                    <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#56C596]/5 to-[#5e17eb]/5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-[#5e17eb] animate-pulse"></div>
                          <h3 className="text-lg font-semibold text-gray-800">Notifications</h3>
                          {unreadCount > 0 && (
                            <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                              {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-sm text-[#5e17eb] hover:text-[#5e17eb]/80 font-medium transition-colors duration-200 hover:underline"
                          >
                            Tout marquer lu
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {loadingNotifications ? (
                        <div className="p-8 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5e17eb] mx-auto"></div>
                          <p className="mt-3 text-sm">Chargement des notifications...</p>
                        </div>
                      ) : !notifications || notifications.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                            <FiBell size={32} className="text-gray-300" />
                          </div>
                          <p className="text-sm">Aucune notification</p>
                          <p className="text-xs text-gray-400 mt-1">Les notifications apparaîtront ici</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {notifications.slice(0, 5).map((notification) => (
                            <div
                              key={notification.id}
                              className={`
                                p-4 hover:bg-gray-50 transition-all duration-200 relative cursor-pointer
                                ${!notification.est_lu ? 'bg-[#5e17eb]/5' : ''}
                                group/notification
                              `}
                              onClick={() => {
                                if (!notification.est_lu) {
                                  markAsRead(notification.id)
                                }
                              }}
                            >
                              {!notification.est_lu && (
                                <>
                                  <span className="absolute left-2 top-1/2 transform -translate-y-1/2 h-2 w-2 bg-[#5e17eb] rounded-full"></span>
                                  <div className="absolute inset-0 bg-[#5e17eb]/5 opacity-0 group-hover/notification:opacity-100 transition-opacity rounded-lg"></div>
                                </>
                              )}

                              <div className="flex items-start gap-3 pl-4 relative z-10">
                                <div className="flex-shrink-0 mt-1">
                                  <div className={`
                                    w-8 h-8 rounded-full flex items-center justify-center
                                    ${!notification.est_lu ? 'bg-[#5e17eb]/10' : 'bg-gray-100'}
                                  `}>
                                    {getNotificationIcon(notification.type)}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className={`
                                      text-sm font-medium 
                                      ${!notification.est_lu ? 'text-gray-900 font-semibold' : 'text-gray-700'}
                                    `}>
                                      {notification.titre}
                                    </h4>
                                    <span className="text-xs text-gray-400 whitespace-nowrap">
                                      {formatTimeAgo(notification.created_at)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                    {notification.message}
                                  </p>

                                  {notification.action_requise && notification.type === 'invitation_reunion' && (
                                    <div className="flex gap-2 mt-3">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          respondToInvitation(notification.id, 'accepte')
                                        }}
                                        disabled={processingInvitation === notification.id}
                                        className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-lg text-xs hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center gap-1 disabled:opacity-50 shadow-sm hover:shadow"
                                      >
                                        {processingInvitation === notification.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                        ) : (
                                          <>
                                            <FiCheck size={12} /> Accepter
                                          </>
                                        )}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          respondToInvitation(notification.id, 'refuse')
                                        }}
                                        disabled={processingInvitation === notification.id}
                                        className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center justify-center gap-1 disabled:opacity-50 shadow-sm hover:shadow"
                                      >
                                        {processingInvitation === notification.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                        ) : (
                                          <>
                                            <FiX size={12} /> Refuser
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {notifications && notifications.length > 5 && (
                      <div className="p-3 border-t border-gray-100 bg-gray-50">
                        <button
                          onClick={() => {
                            setShowNotifications(false)
                            navigate('/notifications')
                          }}
                          className="w-full text-sm text-[#5e17eb] hover:text-[#5e17eb]/80 font-medium flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-[#5e17eb]/5 transition-all duration-200"
                        >
                          Voir toutes les notifications
                          <span className="bg-[#5e17eb]/10 px-2 py-0.5 rounded-full text-xs">
                            {notifications.length}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-3 group bg-gray-50 p-1.5 pl-2 pr-4 rounded-xl hover:bg-gradient-to-r hover:from-[#5e17eb]/10 hover:to-[#5e17eb]/10 transition-all duration-300"
                >
                  <div className="relative">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={authData?.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm group-hover:shadow-md transition-all"
                        onError={(e) => {
                          e.target.onerror = null
                          setPhotoUrl(null)
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] text-white flex items-center justify-center font-bold text-lg shadow-sm group-hover:shadow-md transition-all">
                        {getInitial()}
                      </div>
                    )}
                    <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-[#5e17eb] transition-colors duration-300">
                      {authData?.name || "Utilisateur"}
                    </span>
                    <span className="text-xs text-gray-500">{authData?.role || "Non connecté"}</span>
                  </div>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          navigate("/profile")
                          setShowDropdown(false)
                        }}
                        className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-[#56C596]/10 hover:to-[#5e17eb]/10 hover:text-[#5e17eb] rounded-lg transition-all duration-200"
                      >
                        <span className="text-sm font-medium">Voir Profil</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200"
                      >
                        <FiLogOut className="mr-3 text-red-500" size={16} />
                        <span className="text-sm font-medium">Déconnexion</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-96 max-w-[90vw] animate-in zoom-in-95 duration-300">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-4">
                <FiLogOut size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Confirmer la déconnexion</h3>
              <p className="text-gray-600">Êtes-vous sûr de vouloir vous déconnecter ?</p>
            </div>
            <div className="flex justify-center space-x-4 mt-6">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition duration-150 ease-in-out font-medium text-gray-700"
              >
                Annuler
              </button>
              <button
                onClick={confirmLogout}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white px-5 py-2.5 rounded-xl hover:shadow-lg transition duration-150 ease-in-out font-medium"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        .animate-ping {
          animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes slide-in-from-top-2 {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-in {
          animation-duration: 0.2s;
          animation-fill-mode: both;
        }
        .slide-in-from-top-2 {
          animation-name: slide-in-from-top-2;
        }
        .fade-in {
          animation-name: fade-in;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .zoom-in-95 {
          animation-name: zoom-in-95;
        }
        @keyframes zoom-in-95 {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout